import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6">
                <div className="text-center space-y-3">
                    <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
                    <h1 className="font-display text-2xl">Invalid reset link</h1>
                    <p className="text-muted-foreground text-sm">This link is missing a token. Please request a new one.</p>
                    <Link to="/forgot-password" className="text-sm text-foreground underline underline-offset-2">Request new link</Link>
                </div>
            </div>
        );
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) { setError("Passwords don't match."); return; }
        setError("");
        setLoading(true);
        try {
            await api.post("/auth/confirm-password-reset", { token, new_password: password });
            setDone(true);
        } catch (err) {
            setError(err.response?.data?.detail || "Reset failed. The link may have expired.");
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6">
                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <div className="h-16 w-16 rounded-2xl bg-brand-gradient flex items-center justify-center shadow-mx-md">
                            <CheckCircle2 className="h-8 w-8 text-white" />
                        </div>
                    </div>
                    <h1 className="font-display text-3xl tracking-tight">Password updated</h1>
                    <p className="text-muted-foreground text-sm">You can now sign in with your new password.</p>
                    <Button onClick={() => navigate("/login")} className="mx-btn-primary h-11 px-8 mt-2">
                        Sign in
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="w-full max-w-md">
                <h1 className="font-display text-4xl tracking-tight leading-tight">Choose a new password</h1>
                <p className="mt-3 text-muted-foreground text-sm">
                    At least 8 characters, with both letters and numbers.
                </p>

                <form onSubmit={onSubmit} className="mt-8 space-y-5">
                    <div>
                        <Label htmlFor="password" className="text-xs uppercase tracking-widest font-bold">New password</Label>
                        <div className="relative mt-2">
                            <Input
                                id="password" type={showPw ? "text" : "password"} required
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••" className="h-12 rounded-xl pr-12"
                            />
                            <button type="button" onClick={() => setShowPw(!showPw)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="confirm" className="text-xs uppercase tracking-widest font-bold">Confirm password</Label>
                        <Input
                            id="confirm" type="password" required
                            value={confirm} onChange={(e) => setConfirm(e.target.value)}
                            placeholder="••••••••" className="mt-2 h-12 rounded-xl"
                        />
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-xl px-4 py-3">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            {error}
                        </div>
                    )}

                    <Button type="submit" disabled={loading} className="mx-btn-primary h-12 w-full text-base">
                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Set new password"}
                    </Button>
                </form>

                <p className="mt-6 text-center text-xs text-muted-foreground">
                    Link expired?{" "}
                    <Link to="/forgot-password" className="text-foreground underline underline-offset-2">Request a new one</Link>
                </p>
            </div>
        </div>
    );
}
